import * as React from "react";
import { toast } from "@grandvisionhq/common";
import { useConfiguratorContext } from "@grandvisionhq/configurator";
import { useDispatch, useSelector, useFeatureToggles, statusSuccess } from "@grandvisionhq/state";
import { useUpload, useUploadHandler, Types } from "@grandvisionhq/upload";
import "../../../../../../../../state/state/actions.js";
import { PRESCRIPTION_STEP_UPLOAD_ERROR_ID, RX_SCOPE, PD_SCOPE } from "../../../../../../../../state/state/constants.js";
import "../../../../../../../../state/add-to-cart/state/add-to-cart/selectors.js";
import "../../../../../../../../state/bundles/state/bundles/selectors.js";
import "../../../../../../../../state/bundles/state/bundles/reducer.js";
import { getSelectedFrame, getPrescription, getPrescriptionType, getPdUploadValidationRequirement } from "../../../../../../../../state/configurator/state/configurator/selectors.js";
import "../../../../../../../../state/configurator/state/configurator/reducer.js";
import "../../../../../../../../state/products/state/products/selectors.js";
import "../../../../../../../../state/products/state/products/reducer.js";
import { isNewPrescriptionAndUpload } from "../../../../../../../../utils/utils/is-new-prescription-upload.js";
import { useGetPrescriptionValues } from "../../../../../../hooks/steps/PrescriptionUploadStep/hooks/use-get-prescription-values.js";
import { PrescriptionUploadForm } from "./PrescriptionUploadForm.js";
import { fileUploadResult } from "../../../../../../../../state/data-layer/state/data-layer/actions.js";
import { updateUploadReference, removeUploadReference } from "../../../../../../../../state/configurations/state/configurations/actions.js";
const PrescriptionUploadFormContainer = ({ className }) => {
  const dispatch = useDispatch();
  const { goToNextStep } = useConfiguratorContext();
  const frame = useSelector(getSelectedFrame);
  const { isPrescriptionRecognitionEnabled } = useFeatureToggles();
  const prescription = useSelector(getPrescription);
  const prescriptionType = useSelector(getPrescriptionType);
  const pdUploadValidationRequirement = useSelector(
    getPdUploadValidationRequirement
  );
  const { scopes, setError, configs } = useUpload();
  const { lastError, state, handlers, checkMissingFiles } = useUploadHandler({
    sharedErrorElementId: PRESCRIPTION_STEP_UPLOAD_ERROR_ID,
    configs: scopes.map((scope) => {
      const ref = statusSuccess(prescription) ? prescription.result.uploadReferences?.[scope] : void 0;
      const isPdUploadRequired = Boolean(pdUploadValidationRequirement?.isRequired);
      const required = scope === PD_SCOPE ? isPdUploadRequired : true;
      return {
        scope,
        required,
        uploadStatus: ref ? Types.uploadStatusSuccess(ref) : void 0
      };
    })
  });
  const [fetchRxRecognitionValues] = useGetPrescriptionValues(frame?.sku);
  React.useEffect(() => {
    if (configs[RX_SCOPE]?.status !== "success") {
      dispatch(fileUploadResult("upload not possible - rx config"));
    }
    if (configs[PD_SCOPE]?.status !== "success") {
      dispatch(fileUploadResult("upload not possible - pd config"));
    }
  }, [configs, dispatch]);
  React.useEffect(() => {
    setError(lastError ?? null);
  }, [lastError]);
  const handleNextStepClick = React.useCallback(
    (e) => {
      e?.preventDefault();
      goToNextStep();
      const hasMissing = checkMissingFiles();
      if (hasMissing) {
        const isPDMissing = !Types.isUploadSuccess(state[PD_SCOPE]);
        const isRXMissing = !Types.isUploadSuccess(state[RX_SCOPE]) && pdUploadValidationRequirement?.isRequired;
        if (!isPDMissing && !isRXMissing) return;
        if (isPDMissing && isRXMissing) {
          dispatch(fileUploadResult("files missing"));
        } else {
          dispatch(fileUploadResult(isRXMissing ? "rx missing" : "pd missing"));
        }
      }
    },
    [goToNextStep, checkMissingFiles, state, dispatch, pdUploadValidationRequirement?.isRequired]
  );
  const handleStatusChange = React.useCallback(
    (callbackStatus, scope) => {
      if (!frame) {
        return;
      }
      if (Types.isUploadSuccess(callbackStatus)) {
        toast.success("products.glasses.upload.success.message");
        dispatch(
          fileUploadResult(
            scope === PD_SCOPE ? "pd success" : "rx success"
          )
        );
        if (isPrescriptionRecognitionEnabled && scope === RX_SCOPE && !isNewPrescriptionAndUpload(prescriptionType)) {
          const { storageId, storageToken } = callbackStatus.uploadReference;
          fetchRxRecognitionValues(storageId, storageToken);
        }
        dispatch(
          updateUploadReference({
            frameSku: frame.sku,
            scope,
            uploadReference: callbackStatus.uploadReference
          })
        );
      }
      const removeRxValues = !isNewPrescriptionAndUpload(prescriptionType);
      if (Types.isUploadIdleAfterFileRemove(callbackStatus)) {
        dispatch(
          fileUploadResult(
            scope === PD_SCOPE ? "pd remove" : "rx remove"
          )
        );
        dispatch(
          removeUploadReference({
            frameSku: frame.sku,
            scope,
            removeRxValues
          })
        );
      }
      if (Types.isUploadError(callbackStatus)) {
        dispatch(fileUploadResult(callbackStatus.error));
        dispatch(
          removeUploadReference({
            frameSku: frame.sku,
            scope,
            removeRxValues
          })
        );
      }
      return handlers[scope](callbackStatus);
    },
    [frame, handlers, dispatch, isPrescriptionRecognitionEnabled, fetchRxRecognitionValues]
  );
  if (!statusSuccess(prescription) || !frame) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(
    PrescriptionUploadForm,
    {
      className,
      onSubmit: handleNextStepClick,
      onStatusChange: handleStatusChange,
      uploadState: state,
      showPdUpload: pdUploadValidationRequirement?.isRequired
    }
  );
};
export {
  PrescriptionUploadFormContainer
};
//# sourceMappingURL=PrescriptionUploadFormContainer.js.map
